/**
 * AthleteVisibilityControl — per-take "Athlete-visible" toggle + consent step
 * for Example Performances (backlog/example-performances-template-resources.md,
 * PR 1+2, owner-approved 2026-09-23).
 *
 * Rendered inside CalibrationTakeEditForm.tsx for a single take. Owns its
 * OWN mutation (via useUpdateCalibrationTake) independent of that form's
 * batched "Save" button — visibility is a separate, immediate-effect action,
 * not a field that waits to be bundled with lead-side/dimension/grade edits.
 *
 * Contract (EXAMPLE_PERFORMANCES_PR2_CONTRACT.md):
 *  - Turning the toggle ON opens a consent step. It does NOT call the API by
 *    itself. The consent step shows the EXACT copy in
 *    `ATHLETE_VISIBILITY_CONSENT_COPY` below, plus a 500-char `athlete_note`
 *    textarea with a counter, and a "Confirm" button that sends
 *    `{ is_athlete_visible: true, consent_confirmed: true, athlete_note }`.
 *    `consent_confirmed: true` is NEVER sent by anything other than a click
 *    on that Confirm button.
 *  - Turning the toggle OFF (only reachable once the take is already
 *    server-side visible) sends `{ is_athlete_visible: false }` immediately
 *    — no confirmation step, matching the contract's "OFF sends
 *    {is_athlete_visible:false}".
 *  - The consent timestamp (`visibility_consent_given_at`) is shown whenever
 *    set.
 *
 * Extension beyond the literal contract text (documented, not silent): once
 * a take is already visible, an "Edit note" affordance reopens the same
 * textarea/Confirm UI so a coach can revise the note without an off/on
 * round-trip. It still only ever fires the Confirm payload on an explicit
 * button click — never automatically.
 */

import { useState } from 'react';
import { Button, Alert, Spinner } from '@/components/ui';
import { useUpdateCalibrationTake } from '@/hooks/useCalibrationTakes';
import { formatDate } from '@/utils/formatters';
import { getErrorMessage } from '@/utils/errors';
import { cn } from '@/utils/cn';
import type { CalibrationTake } from '@/types/calibrationTake';

export const ATHLETE_VISIBILITY_CONSENT_COPY =
  "Channel members will be able to see this take's video and the note below. The coach reason text stays private.";

export const MAX_ATHLETE_NOTE_LENGTH = 500;

interface AthleteVisibilityControlProps {
  templateId: string;
  take: CalibrationTake;
}

export default function AthleteVisibilityControl({
  templateId,
  take,
}: AthleteVisibilityControlProps) {
  const updateMutation = useUpdateCalibrationTake(templateId);
  const isVisible = !!take.is_athlete_visible;

  // Local UI state only — never mirrors server truth until a mutation
  // actually resolves. `pendingOn` is true only during the initial
  // "turning on, awaiting Confirm" window (drives the consent-copy line);
  // `panelOpen` covers both that case and "editing the note on an
  // already-visible take".
  const [panelOpen, setPanelOpen] = useState(false);
  const [pendingOn, setPendingOn] = useState(false);
  const [noteInput, setNoteInput] = useState(take.athlete_note ?? '');
  const [error, setError] = useState<string | null>(null);

  const isBusy = updateMutation.isPending;
  const toggleChecked = isVisible || pendingOn;

  const openConsentStep = () => {
    setError(null);
    setNoteInput(take.athlete_note ?? '');
    setPendingOn(true);
    setPanelOpen(true);
  };

  const handleToggleOff = async () => {
    setError(null);
    try {
      await updateMutation.mutateAsync({
        takeId: take.take_id,
        updates: { is_athlete_visible: false },
      });
      setPanelOpen(false);
      setPendingOn(false);
    } catch (err) {
      setError(getErrorMessage(err, 'Could not update visibility. Please try again.'));
    }
  };

  const handleCancelConsent = () => {
    setPendingOn(false);
    setPanelOpen(false);
    setError(null);
    setNoteInput(take.athlete_note ?? '');
  };

  const handleOpenEditNote = () => {
    setNoteInput(take.athlete_note ?? '');
    setPanelOpen(true);
    setError(null);
  };

  const handleConfirm = async () => {
    setError(null);
    try {
      await updateMutation.mutateAsync({
        takeId: take.take_id,
        updates: {
          is_athlete_visible: true,
          consent_confirmed: true,
          athlete_note: noteInput.trim() || null,
        },
      });
      setPendingOn(false);
      setPanelOpen(false);
    } catch (err) {
      setError(getErrorMessage(err, 'Could not save. Please try again.'));
    }
  };

  return (
    <div
      className="space-y-2 border-t border-border-subtle pt-3"
      data-testid={`athlete-visibility-${take.take_id}`}
    >
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-xs font-medium text-text-secondary">Athlete-visible</p>
          <p className="text-xs text-text-muted">
            Share this take&rsquo;s video and note with channel members as an example
            performance.
          </p>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={toggleChecked}
          aria-label="Athlete-visible"
          data-testid={`athlete-visible-toggle-${take.take_id}`}
          disabled={isBusy}
          onClick={() => (toggleChecked ? handleToggleOff() : openConsentStep())}
          className={cn(
            'relative inline-flex h-6 w-11 flex-shrink-0 rounded-full',
            'border-2 border-transparent transition-colors duration-200 ease-in-out',
            'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2',
            'disabled:opacity-50',
            toggleChecked ? 'bg-accent' : 'bg-dark-elevated-2',
          )}
        >
          <span
            className={cn(
              'pointer-events-none inline-block h-5 w-5 transform rounded-full',
              'bg-white shadow ring-0 transition duration-200 ease-in-out',
              toggleChecked ? 'translate-x-5' : 'translate-x-0',
            )}
          />
        </button>
      </div>

      {isVisible && take.visibility_consent_given_at && (
        <p
          className="text-xs text-text-muted"
          data-testid={`athlete-visible-consent-timestamp-${take.take_id}`}
        >
          Consent given {formatDate(take.visibility_consent_given_at)}
        </p>
      )}

      {isVisible && !panelOpen && (
        <div className="space-y-1">
          {take.athlete_note ? (
            <p className="text-xs text-text-muted italic whitespace-pre-wrap break-words">
              &ldquo;{take.athlete_note}&rdquo;
            </p>
          ) : (
            <p className="text-xs text-text-muted">No note added yet.</p>
          )}
          <button
            type="button"
            data-testid={`athlete-note-edit-${take.take_id}`}
            onClick={handleOpenEditNote}
            className="text-xs font-medium text-accent hover:underline"
          >
            Edit note
          </button>
        </div>
      )}

      {panelOpen && (
        <div className="space-y-2 rounded-lg border border-border-subtle bg-dark-elevated-2 p-3">
          {pendingOn && (
            <p
              className="text-xs text-text-secondary"
              data-testid={`athlete-visibility-consent-copy-${take.take_id}`}
            >
              {ATHLETE_VISIBILITY_CONSENT_COPY}
            </p>
          )}
          <label
            htmlFor={`athlete-note-${take.take_id}`}
            className="text-xs font-medium text-text-secondary block"
          >
            Note shown to athletes{' '}
            <span className="text-text-muted font-normal">(optional)</span>
          </label>
          <textarea
            id={`athlete-note-${take.take_id}`}
            data-testid={`athlete-note-input-${take.take_id}`}
            value={noteInput}
            onChange={(e) => setNoteInput(e.target.value.slice(0, MAX_ATHLETE_NOTE_LENGTH))}
            rows={3}
            className="w-full px-2 py-1.5 border border-border-subtle rounded bg-dark-elevated-1
              text-text-primary text-xs focus:outline-none focus:ring-1 focus:ring-accent resize-y"
          />
          <p
            className="text-xs text-text-muted text-right"
            data-testid={`athlete-note-counter-${take.take_id}`}
          >
            {noteInput.length} / {MAX_ATHLETE_NOTE_LENGTH}
          </p>

          {error && <Alert variant="error">{error}</Alert>}

          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="primary"
              size="sm"
              data-testid={`athlete-visibility-confirm-${take.take_id}`}
              onClick={handleConfirm}
              disabled={isBusy}
            >
              {isBusy ? (
                <span className="flex items-center gap-1.5">
                  <Spinner size="sm" />
                  Saving...
                </span>
              ) : (
                'Confirm'
              )}
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              data-testid={`athlete-visibility-cancel-${take.take_id}`}
              onClick={handleCancelConsent}
              disabled={isBusy}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
